import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createSupervisionMeeting, CreateSupervisionMeetingBody, getProviderSupervisee, getProviderSupervisees, getSupervisionMeeting, getSupervisionMeetings } from '@uplift/api';

import { providerQueryKeys } from './providerQueryKeys';


export const useProviderSuperviseesQuery = (providerId: string) => {
  return useSuspenseQuery({
    queryKey: providerQueryKeys.supervisees(providerId),
    queryFn: async () => {
      const response = await getProviderSupervisees(providerId);

      return response.data;
    },
  });
};


export const useProviderSuperviseeQuery = (providerId: string, matchId: string) => {
  return useSuspenseQuery({
    queryKey: providerQueryKeys.supervisee(providerId, matchId),
    queryFn: async () => {
      const response = await getProviderSupervisee(providerId, matchId);

      return response.data;
    },
  });
};


export const useSupervisionMeetingsQuery = (providerId: string, matchId: string) => {
  return useQuery({
    queryKey: providerQueryKeys.supervisionMeetings(providerId, matchId),
    queryFn: async () => {
      const response = await getSupervisionMeetings(providerId, matchId);

      return response.data;
    },
    enabled: !!providerId && !!matchId,
  });
};


export const useSupervisionMeetingQuery = (providerId: string, matchId: string, meetingId: string) => {
  return useQuery({
    queryKey: providerQueryKeys.supervisionMeeting(providerId, matchId, meetingId),
    queryFn: async () => {
      const response = await getSupervisionMeeting(providerId, matchId, meetingId);

      return response.data;
    },
    enabled: !!providerId && !!matchId && !!meetingId,
  });
};


export const useCreateSupervisionMeetingMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: { providerId: string; matchId: string } & CreateSupervisionMeetingBody) => {
      const { providerId, matchId, ...body } = args;

      return createSupervisionMeeting(providerId, matchId, body);
    },
    async onSuccess(_data, variables) {
      await Promise.all([
        queryClient.invalidateQueries(providerQueryKeys.supervisees(variables.providerId)),
        queryClient.invalidateQueries(providerQueryKeys.supervisionMeetings(variables.providerId, variables.matchId)),
      ]);
    },
  });
};
